import { useEffect, useState } from "react";

/**
 * Two viewing modes for the evaluation-settings surface, driven by the two
 * personas using the page:
 *
 *   chro — only the CHRO's daily-use sections in priority order:
 *          Calibração first (run + review), then Avaliações arquivadas,
 *          then Disciplinas. Everything else is hidden so the CHRO is not
 *          drowned in unrelated controls. This is the default.
 *
 *   full — every section visible in the original "system overview" order
 *          (Disciplinas → Histórico → Calibração → Engenharia). Engineers
 *          and platform owners flip to this when they need the prompt
 *          pipeline, backup tooling, or prompt-health diagnostics.
 *
 * The choice persists in localStorage so an engineer's flip survives a
 * reload but a CHRO never lands in the engineering view by accident.
 */
export type PageAudience = "chro" | "full";

const STORAGE_KEY = "eval-settings:audience";

function readInitial(): PageAudience {
  if (typeof window === "undefined") return "chro";
  const saved = window.localStorage.getItem(STORAGE_KEY);
  return saved === "full" ? "full" : "chro";
}

export function usePageAudience(): readonly [PageAudience, (next: PageAudience) => void] {
  const [audience, setAudience] = useState<PageAudience>(readInitial);
  // Sync across tabs/windows. If an operator opens the page in two tabs
  // and toggles in one, the other reflects the change without a reload.
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      const next = e.newValue === "full" ? "full" : "chro";
      setAudience(next);
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);
  const update = (next: PageAudience) => {
    setAudience(next);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, next);
    }
  };
  return [audience, update] as const;
}
